import React, { useEffect, useState } from "react";
import circle from "./circle.png";
import spinner from "./spinner.gif";
import Navbar from "./Navbar";
import "./UpcomingCricketScore.css";
import PageTitle from "./PageTitle";
import logo from "./logo.png";
import Header from "./Header";

const UpcomingCricketScore = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [data, setData] = useState([]);
  const [inputData, setInputData] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const itemsPerPage = 12;
  
  // New state to track the time when the data was last fetched
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  const cacheDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

  const urls = [
    `https://api.cricapi.com/v1/cricScore?apikey=0328c2e4-976d-4f98-a46c-16b370991bbc`, // Primary URL
    `https://api.cricapi.com/v1/cricScore?apikey=9b1921c5-88c2-426c-a812-cc491f063b05`, // Fallback URL 1
    `https://api.cricapi.com/v1/cricScore?apikey=dd9cb3ec-87c4-48b2-b159-3fee40ad2efa`, // Fallback URL 2
    `https://api.cricapi.com/v1/cricScore?apikey=3d7208ab-5bf3-46c4-92f8-565d261bd110`, // Fallback URL 3
  ];

  // Function to check if cache is still valid
  const isCacheValid = () => {
    if (!lastFetchTime) return false;
    const currentTime = new Date().getTime();
    return currentTime - lastFetchTime < cacheDuration;
  };

  const getData = async () => {
    if (isCacheValid()) {
      console.log("Using cached data");
      return; // Don't fetch new data if cache is still valid
    }

    setLoading(true);
    try {
      let dataFetched = false;
      for (const url of urls) {
        const response = await fetch(url);
        const result = await response.json();
        if (result && result.data && result.data.length > 0) {
          setData(result.data); // Set data if present
          setLastFetchTime(new Date().getTime()); // Update last fetch time
          dataFetched = true;
          break; // Exit the loop if data is fetched
        }
      }
      if (!dataFetched) {
        console.warn("No data found from any of the URLs.");
        setData([]); 
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    getData();
  }, []);

  const handleInput = (e) => {
    setInputData(e.target.value);
  };

  const handleBtn = () => {
    setSearch(inputData);
    setCurrentPage(1);
  };

  const handlePagination = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDateChange = (e, type) => {
    if (type === "start") setStartDate(e.target.value);
    if (type === "end") setEndDate(e.target.value);
  };

  // Filter the data based on the search input, status, and date range
  const filteredData = data?.filter((curVal) => {
    const matchDate = new Date(curVal.dateTimeGMT);
    const isWithinDateRange =
      (!startDate || matchDate >= new Date(startDate)) &&
      (!endDate || matchDate <= new Date(endDate));

    return (
      curVal.status === "Match not started" &&
      (curVal.series.toLowerCase().includes(search.toLowerCase()) ||
        curVal.t1.toLowerCase().includes(search.toLowerCase()) ||
        curVal.t2.toLowerCase().includes(search.toLowerCase())) &&
      isWithinDateRange
    );
  });

  // Sort matches to prioritize today's matches
  const sortedFilteredData = filteredData?.sort((a, b) => {
    const aDate = new Date(a.dateTimeGMT);
    const bDate = new Date(b.dateTimeGMT);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (aDate >= today && bDate < today) return -1;
    if (aDate < today && bDate >= today) return 1;

    return aDate - bDate;
  });

  const totalPages = Math.ceil(sortedFilteredData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedFilteredData?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const visiblePages = (pageNumbers) => {
    const range = 3;
    const start = Math.max(0, currentPage - range - 1);
    const end = Math.min(totalPages, currentPage + range);
    return pageNumbers.slice(start, end);
  };

  const convertToIST = (gmtDateTime) => {
    const date = new Date(gmtDateTime);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + istOffset);

    const formattedDate = istDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const formattedTime = istDate.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
  };

  return (
    <>
      <Navbar />
      <PageTitle
        title="Upcoming Cricket Matches - Live Scores & Match Schedules"
        description="Get the latest updates on upcoming cricket matches, including live scores, schedules, and series information. Stay informed with our detailed cricket match schedules."
        url="upcoming-matches"
      />
      <Header />

      <div className="main-container">
        <div className="searchBar">
          <input
            type="text"
            placeholder="Search Match, Series"
            onChange={handleInput}
            value={inputData}
          />
          <button onClick={handleBtn}>Search</button>
        </div>

        <div className="date-filters-container">
          <div className="date-filter">
            <label htmlFor="start-date">Start Date:</label>
            <input
              type="date"
              id="start-date"
              onChange={(e) => handleDateChange(e, "start")}
              value={startDate}
            />
          </div>
          <div className="date-filter">
            <label htmlFor="end-date">End Date:</label>
            <input
              type="date"
              id="end-date"
              onChange={(e) => handleDateChange(e, "end")}
              value={endDate}
            />
          </div>
        </div>

        <div className="heading">
          <img src={circle} alt="Logo" />
          <p>Upcoming Cricket Matches & Scores</p>
        </div>

        {loading ? (
          <div className="loading">
            <img src={spinner} alt="Loading..." />
            <p>Fetching live data...</p>
          </div>
        ) : (
          <div className="container">
            {paginatedData?.length > 0 ? (
              paginatedData?.map((curVal, index) => (
                <div className="card" key={index}>
                  <h3>{curVal.series}</h3>
                  <h3>{curVal.matchType}</h3>
                  <div className="img">
                    <div>
                      <img src={curVal.t1img || logo} alt="Team 1" />
                      <p>{curVal.t1}</p>
                    </div>
                    <div>
                      <img src={curVal.t2img || logo} alt="Team 2" />
                      <p>{curVal.t2}</p>
                    </div>
                  </div>
                  <p className="date-time">
                    Match Date & Time: {convertToIST(curVal.dateTimeGMT)}
                  </p>
                  <p className="status">Status: {curVal.status}</p>
                </div>
              ))
            ) : (
              <p>Data Not Found!</p>
            )}
          </div>
        )}

        {filteredData?.length > 0 && !loading && (
          <div className="pagination">
            <button
              onClick={() => handlePagination(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {currentPage > 4 && (
              <button onClick={() => handlePagination(1)}>1</button>
            )}
            {currentPage > 5 && <span>...</span>}

            {visiblePages(pageNumbers).map((page) => (
              <button
                key={page}
                onClick={() => handlePagination(page)}
                className={currentPage === page ? "active" : ""}
              >
                {page}
              </button>
            ))}

            {currentPage < totalPages - 4 && <span>...</span>}
            {currentPage < totalPages - 3 && (
              <button onClick={() => handlePagination(totalPages)}>
                {totalPages}
              </button>
            )}

            <button
              onClick={() => handlePagination(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default UpcomingCricketScore;
